import { dateFromInput, DateRange, utcDateString } from './date-time';
import { formApi, FormApi, providerField } from './form';

import { ReactElement } from 'react';
import create, { UseStore } from 'zustand';

import { Subscription } from './api';
import { auditBatch } from './audit-batch-api';
import { DateInline, Switch, Tags, Text } from './controls';
import { knownCodingstates, Provider } from './domain';
import { HBox } from './layout';
import { createPageApi, PageApi, TableColumn } from './paged-table';
import { organizationData } from './visit-context';

type Activity = 'AUDIT' | 'CODEREVIEW' | 'CODING';

type ActivityArgs = {
  activity: Activity | string;
};
type Org = {
  organizationCode: string;
  organizationName: string;
  locations?: any[];
};
type Loc = {
  locationCode: string;
  locationName: string;
  departments?: any[];
};
type Dep = {
  departmentCode: string;
  departmentName: string;
};

type OrgSelectArgs = {
  onError?: (error?: string) => void;
  updateStorage?: boolean;
};
type Location = {
  organizationCode: string | string[];
  locationCode: string | string[];
  departmentCode: string | string[];
  organizationName?: string;
};

export type OrgSelection = Location & {
  activities: string[];
  activity: string;
  promptOrg: boolean;
};

type OrgSelectActions = {
  getActivities: () => Promise<string[]>;
  setActivity: (activity: string, promptOrg?: boolean) => Promise<Org[]>;
  reset: () => void;
  prompt: (onSuccess: () => void) => void;
  selected: () => void;
  shouldReload: () => boolean;
  storeLocal: () => void;
  loadLocal: () => void;
  getUrlParams: (target?: string) => URL;
  setUrlParams: (target?: string) => void;
};

type OrgSelectForms = {
  organization: FormApi;
  location: FormApi;
  department: FormApi;
};

export type OrgSelectApi = {
  data: UseStore<OrgSelection>;
  actions: OrgSelectActions;
  forms: OrgSelectForms;
  events: Subscription<OrgSelection>;
};

type Field = {
  fieldName: string;
  sortOrder?: 1 | -1; // 1 = ascending, -1 = descending
  stringFilter?: string;
  integerFilter?: number;
  floatFilter?: number;
  booleanFilter?: boolean | [boolean, boolean];
};

type Filters = {
  predicate: 'AND' | 'OR';
  fields: Field[];
};

export type LocationQuery = {
  locationCode?: string | string[];
  departmentCode?: string | string[];

  searchId?: string; // id of the dashboard search,
  detailView?: string; // "visits || documents || worksheets",

  startDate?: string; // optional
  endDate?: string; // optional

  queryString?: string;

  sort?: Field[];
  fieldName?: string;
  filters?: Filters;

  pageSize?: number;
  pageNumber?: number;

  visitSearchScheme?: string;
  documentSearchScheme?: string;
  worksheetSearchScheme?: string;
  searchScheme?: string;
};

export interface SortField {
  fieldName: string;
  sortOrder?: 1 | -1;
}

export interface SearchField extends SortField {
  startDate?: string;
  endDate?: string;
  stringFilter?: string | string[] | any;
  integerFilter?: number;
  floatFilter?: number;
  booleanFilter?: boolean;
}

export function searchField(
  fieldName: string,
  field?: Partial<SearchField>
): SearchField {
  return { ...{ fieldName }, ...field };
}

export const locationField = searchField('locationCode');
export const accountField = searchField('accountNumber');
export const registrationDateField = searchField('registrationDate');
export const serviceDateField = searchField('serviceDate');
export const createDateField = searchField('createDate');
export const createLocalField = searchField('createLocal');
export const mrnField = searchField('mrn');
export const firstDateInCodingField = searchField('firstDateInCoding');
export const worksheetStateField = searchField('state');
export const notWorksheetStateField = searchField('!state');

export const worksheetIdField = searchField('worksheetId');
export const departmentField = searchField('departmentCode');
export const ownerFirstField = searchField('owningProvider.firstName');
export const ownerLastField = searchField('owningProvider.lastName');
export const providerId = searchField('owningProvider.providerId');
export const workflowOwnersLastField = searchField(
  'workflowOwners[0].lastName'
);

export const providerFirstField = searchField('provider.firstName');
export const providerLastField = searchField('provider.lastName');

export const patientFirstField = searchField('patient.firstName');
export const patientLastField = searchField('patient.lastName');
export const patientDobField = searchField('patient.birthDate');
export const insuranceField = searchField('insurances.companyName');

export const hasCompleteDemosField = searchField('hasCompleteDemos');
export const hasCompleteBundleField = searchField('hasCompleteBundle');
export const hasWorksheetsField = searchField('hasWorksheets');

export const missingRequiredDocumentTypes = searchField(
  'missingRequiredDocumentTypes'
);

export const complete = searchField('complete');
export const markedForDelete = searchField('markedForDelete');
export const completedDemos = searchField('completedDemos');
export const incompleteDemos = searchField('incompleteDemos');
export const completeBundles = searchField('completeBundles');
export const incompleteBundles = searchField('incompleteBundles');
export const completeWorksheets = searchField('completeWorksheets');
export const incompleteWorksheets = searchField('incompleteWorksheets');
export const completedBilling = searchField('completedBilling');
export const incompleteBilling = searchField('incompleteBilling');
export const bundleVersion = searchField('bundleVersion');

export const missingPatientBirthDate = searchField(
  'missingElements.patient.birthDate'
);

export const visitsPatientLast = searchField('visits.patientLast');
export const visitsPatientFirst = searchField('visits.patientFirst');
export const visitsMrn = searchField('visits.mrn');
export const visitsAccountNumber = searchField('visits.accountNumber');

export const hasCompleteBundle = searchField('hasCompleteBundle');
export const hasWorksheets = searchField('hasWorksheets');
export const hasBilledProcedures = searchField('hasBilledProcedures');
export const missingVisitElements = searchField('missingVisitElements');
export const visitId = searchField('visitId');

export const hasCompleteDemos = searchField('hasCompleteDemos');
export const workflow = searchField('workflow');

type VisitSearchQuery = LocationQuery;
type SearchPredicate = 'AND' | 'OR';

export interface SearchFieldCriteria {
  field: SearchField;
  form?: FormApi<{ control: any }>;
  color?: string;
  label?: string;
  icon?: ReactElement;
  editable?: boolean;
  dateTime?: boolean;
  api?: DashboardSearchApi;
  startDate?: Date;
  endDate?: Date;
  parseInt?: boolean;
  valueLabel?: (v?: any) => string;
  editor?: (c: SearchFieldCriteria) => JSX.Element;
  setValue?: (a: SearchField) => void;
  setList?: (a: any[]) => void;
  reset?: () => void;
}

export type DashboardSearchConfiguration = {
  query: VisitSearchQuery;
  fields: SearchFieldCriteria[];
  filters: SearchFieldCriteria[];
  sort: SearchFieldCriteria[];
  features?: any[];
};

export type SearchResult<T = any> = {
  rows: T[];
  pageNumber?: number;
  pageSize?: number;
  totalRecords?: number;
};

type SearchData<T = any> = DashboardSearchConfiguration & {
  selectedFilter: SearchFieldCriteria;
  openFilter: boolean;
  period: PeriodSearch;
  advancedActive: boolean;
  providers: Provider[];
  stateFilters: { label: string; name: string }[];
  result: SearchResult<T>;
};

interface PeriodSearch {
  period?: DateRange;
  search?: string;
}

type SearchActions = {
  setQuery(v: Partial<VisitSearchQuery>): void;
  updateOrAddFilter(field: SearchField, valueFn?: () => any): SearchField;
  updateOrAddSort(field: SearchField): SearchField;
  updateColumnSort(field: SearchField): SearchField;
  search(): Promise<SearchResult>;
  reset(): void;
  hasFilter: (c: SearchFieldCriteria) => boolean;
  openFilter: (c: SearchFieldCriteria) => void;
  closeFilter: () => void;
  deleteFilter: (c: SearchFieldCriteria) => void;
  addFilter: (c: SearchFieldCriteria) => void;
  refreshFilters: () => void;
  updateFilter: (c: SearchFieldCriteria) => void;
  addSort: (c: SearchFieldCriteria) => void;
  updateSort: (c: SearchFieldCriteria) => void;
  hasSort: (c: SearchFieldCriteria) => boolean;
  deleteSort: (c: SearchFieldCriteria) => void;
  toggleAdvanced: () => void;
  openIfFilters: () => void;
  resetToFirstPage: () => void;
  addFilters: (a: SearchField[], append?: boolean) => void;
  findCriteria: (fieldName: string) => SearchFieldCriteria;
};

type SearchForms = {
  predicate: FormApi<{ predicate: 'OR' | 'AND' }>;
};
type Page = {
  pageNumber: number;
  pageSize: number;
};

//TODO: dynamically access form data, by field key
type DataProvider = {
  [key: string]: () => Promise<any>;
};

export type DashboardSearchApi<T = any> = {
  configuration: DashboardSearchConfiguration;
  data: UseStore<SearchData>;
  actions: SearchActions;
  forms: SearchForms;
  page: PageApi;
  provider: DataProvider;
  onPeriodSearch: (args: PeriodSearch) => void;
  onRequestSort?: (event: any, column: TableColumn<T>) => void;
  onRequestPage?: (page: Page) => void;
  onRowClick?: (row: any) => void;
  rowIsSelected?: (rowData: T) => boolean;
};

function setCriteriaField(a: SearchFieldCriteria, b: SearchField) {
  if (a && b) {
    a.field = b;
  }
}

export function searchFieldCriteria(
  field: SearchField,
  props: Partial<SearchFieldCriteria>
): Partial<SearchFieldCriteria> {
  return {
    field,
    ...props,
  };
}

export function textFormCriteria(
  criteria: Partial<SearchFieldCriteria>
): SearchFieldCriteria {
  const { parseInt } = criteria;

  let field = criteria.field;
  let selectedField: SearchFieldCriteria;

  const form = formApi({
    values: {
      control: '',
    },
    fields: {
      control: {
        required: true,
        label: criteria.label,
        autoFocus: true,
        onEnter: () => {
          form.submit();
        },
      },
    },
    valid: () => true,
    onSubmit: (v) => {
      if (parseInt) {
        field.integerFilter = Number.parseInt(v.control);
      } else {
        field.stringFilter = (v.control || '').trim();
      }

      if (selectedField && form.valid.getState().value) {
        selectedField.api.actions.addFilter(selectedField);
      }
    },
  });
  const { control } = form.controls;
  function setValue(a: SearchField) {
    if (a) {
      setCriteriaField(selectedField, a);

      if (parseInt) {
        field.integerFilter = a.integerFilter;
        field.stringFilter = undefined;
        control.value.setState({ value: field.integerFilter });
      } else {
        field.stringFilter = a.stringFilter;
        control.value.setState({ value: field.stringFilter });
      }
    }
  }
  function reset() {
    control.value.setState({ value: null });
  }
  return {
    ...criteria,
    ...{
      form,
      control,
      setValue,
      reset,
      valueLabel: () => control.value.getState().value || '',
      editor: (v: SearchFieldCriteria) => {
        selectedField = v;
        setValue(v?.field);
        return <Text {...control} />;
      },
    },
  } as SearchFieldCriteria;
}

export function booleanCriteria(
  criteria: Partial<SearchFieldCriteria>
): SearchFieldCriteria {
  let field = criteria.field;
  let selectedField: SearchFieldCriteria;

  const form = formApi({
    values: {
      control: false,
    },
    fields: {
      control: {
        label: criteria.label,
        onChange: (value) => {
          control.value.setState({ value });
          form.setValues({ control: value });
        },
      },
    },
    valid: () => true,
    onSubmit: (values) => {
      const value = values.control;
      field.booleanFilter = value ? true : false;
      selectedField.field = field;

      if (selectedField) {
        selectedField.api.actions.addFilter(selectedField);
      }
    },
  });
  const { control } = form.controls;

  function setValue(a: SearchField) {
    if (a) {
      field.booleanFilter = a.booleanFilter;
      field = a;
      control.value.setState({ value: a.booleanFilter || false });
      control.selected.setState({ value: a.booleanFilter || false });
    }
  }
  function reset() {
    if (field) {
      field.booleanFilter = false;
    }
    control.value.setState({ value: false });
    control.selected.setState({ value: false });
  }
  return {
    ...criteria,
    ...{
      form,
      setValue,
      reset,
      control,
      valueLabel: () => (control.value.getState().value ? 'True' : 'False'),
      editor: (v: SearchFieldCriteria) => {
        selectedField = v;

        setValue(v.field);
        return <Switch {...control} />;
      },
    },
  } as SearchFieldCriteria;
}

export function multiSelectFormCriteria(
  criteria: Partial<SearchFieldCriteria>,
  list: any[]
): SearchFieldCriteria {
  let field = criteria.field;
  let selectedField: SearchFieldCriteria;

  const form: FormApi<{ control: any }> = formApi({
    values: {
      control: [],
    },
    fields: {
      control: {
        required: true,
        label: criteria.label,
        list,
        listLabel: (v) => v.label,
        autoFocus: true,
        style: { width: undefined },
        onChange: (v) => { console.log(v.control) },
        onEnter: () => {
          form.submit();
          if (selectedField && form.valid.getState().value) {
            selectedField.api.actions.addFilter(selectedField);
          }
        },
      },
    },
    valid: () => true,

    onSubmit: (v) => {
      field.stringFilter = (v.control || []).map((u) => u.name).join(',');
    },
  });

  const { control } = form.controls;

  function setValue(a: SearchField) {
    if (a) {
      field = a;
      field.stringFilter = a.stringFilter;

      const existing = control.value.getState().value;
      if (existing?.length) {
        if (existing[0].fieldName === a.fieldName) {
          return;
        }
      }

      const values: any[] = control.list.getState().value;

      if (Array.isArray(field.stringFilter)) {
        const list: string[] = field.stringFilter;
        const items = values.filter((v) => list.find((a) => v.name === a));

        if (items?.length) {
          control.value.setState({ value: items });
        }
      } else {
        const item = values.find(
          (v) => v.name === a.stringFilter || v.label === a.stringFilter
        );
        if (item) {
          control.value.setState({ value: [item] });
        }
      }
    }
  }

  function reset() {
    if (field) {
       field.stringFilter = null;
    }
    control.value.setState({ value: [] });
  }
  function setList(a: any[]) {
    if (a?.length) {
      control.list.setState({ value: a });
    }
  }

  const valueLabel = () => {
    const value = control.value.getState().value;

    if (!Array.isArray(value)) {
      return '';
    }
    return value.map((v: { label: any }) => v.label).join(',');
  };

  return {
    ...criteria,
    ...{
      form,
      control,
      valueLabel,
      setValue,
      setList,
      reset,
      editor: (v: SearchFieldCriteria) => {

        if (v.api) {

          if (v.label === 'Department') {
            let temp = []
            organizationData?.getState()?.departments?.map(item => {
              temp.push({ name: item.code, label: item.code })
            })
            control.list.setState({ value: temp })
            setList(temp)
          } else if (v.label === 'Location') {
            let temp = []
            organizationData?.getState()?.locations?.map(item => {
              temp.push({ name: item.code, label: item.code })
            })
            control.list.setState({ value: temp })
            setList(temp)
          } else if (v.label === 'AuditId') {
            let temp = [];
            auditBatch.data.getState().batches?.entries.map(item => {
              temp.push({ label: item.description, name: item.auditId });
            })
            control.list.setState({ value: temp })
            setList(temp)

          }
          /*
                    // TODO: Api for dynamically setting list after init
                    // example below
                    const filters = v.api.data.getState().stateFilters;
                    if (filters?.length) {
                        control.list.setState({ value: filters });
                    }
                    */
        }

        selectedField = v;
        setValue(v?.field);
        return <Tags {...control} />;
      },
    },
  } as SearchFieldCriteria;
}

export function personFormCriteria(
  criteria: Partial<SearchFieldCriteria>
): SearchFieldCriteria {
  const { field } = criteria;

  let selectedField: SearchFieldCriteria;

  const form = formApi({
    values: {
      control: [],
    },
    fields: {
      control: providerField([], {
        required: true,
        label: criteria.label,
        autoFocus: true,
        style: { width: undefined },
        onEnter: () => {
          form.submit();

          if (selectedField && form.valid.getState().value) {
            selectedField.api.actions.addFilter(selectedField);
          }
        },
      }),
    },
    valid: () => true,
    onSubmit: (v) => {
      field.stringFilter = (v.control || []).map((u) => u.lastName).join(',');
    },
  });
  const { control } = form.controls;
  function setList(a: any[]) {
    if (a?.length) {
      control.list.setState({ value: a });
    }
  }
  function reset() {
    control.value.setState({ value: [] });
  }

  return {
    ...criteria,
    ...{
      form,
      control,
      setList,
      reset,
      valueLabel: () =>
        (control.value.getState().value || []).map((a) => a.lastName).join(','),
      editor: (v: SearchFieldCriteria) => {
        selectedField = v;
        return <Tags {...control} />;
      },
    },
  } as SearchFieldCriteria;
}

export function dateFormCriteria(
  criteria: Partial<SearchFieldCriteria>
): SearchFieldCriteria {
  const { dateTime } = criteria;

  let selectedCriteria: SearchFieldCriteria;

  const onEnter = () => {
    form.submit();
    if (selectedCriteria && form.valid.getState().value) {
      selectedCriteria.api.actions.addFilter(selectedCriteria);
    }
  };

  const form = formApi({
    values: {
      start: null,
      end: new Date(),
    },
    fields: {
      start: {
        autoFocus: true,
        required: true,
        label: 'Start',
        onEnter,
      },
      end: {
        required: true,
        label: 'End',
        onEnter,
      },
    },
    valid: () => true,
    onSubmit: () => {
      const field = selectedCriteria?.field;
      if (!field) {
        return;
      }

      const st = start.value.getState().value || new Date();
      const en = end.value.getState().value || new Date();

      field.startDate = utcDateString(st);
      field.endDate = utcDateString(en);
      form.values.setState({ ...(form.values.getState() as any) });
    },
  });

  const { start, end } = form.controls;
  function setValue(a: SearchField) {
    setCriteriaField(selectedCriteria, a);

    if (a && a.startDate) {
      const st = dateFromInput(a.startDate);
      const en = dateFromInput(a.endDate);

      a.startDate = utcDateString(st || new Date());
      a.endDate = utcDateString(en || new Date());

      start.value.setState({ value: st });
      end.value.setState({ value: en });
    }
  }

  return {
    ...criteria,
    ...{
      form,
      setValue,
      control: end,
      valueLabel: (v: SearchFieldCriteria) => {
        const c = selectedCriteria || v;
        const st = start.value.getState().value;
        const stLabel = st ? utcDateString(st) : c?.field.startDate || '';

        const en = end.value.getState().value;
        const enLabel = en ? utcDateString(en) : c?.field.endDate || '';

        return `${stLabel} to ${enLabel}`;
      },
      editor: (v: SearchFieldCriteria) => {
        selectedCriteria = v;
        return (
          <HBox containerStyle={{ overflow: 'hidden' }}>
            <DateInline {...start} />
            <DateInline {...end} />
          </HBox>
        );
      },
    },
  } as any;
}

const locationCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(locationField, {
      ...{ label: 'Location', editable: true },
      ...props,
    })
  );

export const accountCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(accountField, {
      ...{ label: 'Account', editable: true },
      ...props,
    })
  );

export const createDateCriteria = (props?: Partial<SearchFieldCriteria>) =>
  dateFormCriteria(
    searchFieldCriteria(createDateField, {
      ...{ label: 'Create Date', editable: true },
      ...props,
    })
  );

export const createLocalCriteria = (props?: Partial<SearchFieldCriteria>) =>
  dateFormCriteria(
    searchFieldCriteria(createLocalField, {
      ...{ label: 'Create Local', editable: true },
      ...props,
    })
  );

export const registrationDateCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  dateFormCriteria(
    searchFieldCriteria(registrationDateField, {
      ...{ label: 'Registration Date', editable: true, dateTime: true },
      ...props,
    })
  );

export const serviceDateCriteria = (props?: Partial<SearchFieldCriteria>) =>
  dateFormCriteria(
    searchFieldCriteria(serviceDateField, {
      ...{ label: 'Service', editable: true },
      ...props,
    })
  );

export const mrnCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(mrnField, {
      ...{ label: 'MRN', editable: true },
      ...props,
    })
  );

const firstDateInCodingCriteria = (props?: Partial<SearchFieldCriteria>) =>
  dateFormCriteria(
    searchFieldCriteria(firstDateInCodingField, {
      ...{ label: 'Age', editable: true },
      ...props,
    })
  );

export const worksheetStateCriteria = (props?: Partial<SearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(worksheetStateField, {
      ...{ label: 'State', editable: true },
      ...props,
    }),
    knownCodingstates
  );

export const notWorksheetStateCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  multiSelectFormCriteria(
    searchFieldCriteria(notWorksheetStateField, {
      ...{ label: 'Not State', editable: true },
      ...props,
    }),
    knownCodingstates
  );

const knownMissingVisitElements = [
  { label: 'Insurance', name: 'Insurance' },
  { label: 'Patient Birthdate', name: 'Patient Birthdate' },
];

export const missingVisitElementsCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  multiSelectFormCriteria(
    searchFieldCriteria(missingVisitElements, {
      ...{ label: 'Missing Elements', editable: true },
      ...props,
    }),
    knownMissingVisitElements
  );

const knownWorkflows = [
  { label: 'Coding Review', name: 'codeReviewStd' },
  { label: 'Audit', name: 'auditStd' },
];

export const workflowCriteria = (props?: Partial<SearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(workflow, {
      ...{ label: 'Workflow', editable: true },
      ...props,
    }),
    knownWorkflows
  );

export const worksheetTypeField = searchField('worksheetType');
const knownWorksheetTypes = [
  { label: 'Physician', name: 'PHYSICIAN' },
  { label: 'Facility', name: 'FACILITY' },
];
export const worksheetTypeCriteria = (props?: Partial<SearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(worksheetTypeField, {
      ...{ label: 'Worksheet Type', editable: true },
      ...props,
    }),
    knownWorksheetTypes
  );

//TOOD: null in results
export const workflowActivityField = searchField('workflowActivity');

const knownWorksheetActivities = [
  { label: 'Coding', name: 'CODING' },
  { label: 'Code Review', name: 'CODEREVIEW' },
  { label: 'Audit', name: 'AUDIT' },
];

export const workflowActivityCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  multiSelectFormCriteria(
    searchFieldCriteria(workflowActivityField, {
      ...{ label: 'Activity', editable: true },
      ...props,
    }),
    knownWorksheetActivities
  );

export const activeField = searchField('activeFlag');

const knownActivities = [
  { label: 'Active', name: 'active' },
  { label: 'Billing', name: 'billing' },
  { label: 'Inactive', name: 'inactive' },
];

export const activeFieldCriteria = (props?: Partial<SearchFieldCriteria>) =>
  multiSelectFormCriteria(
    searchFieldCriteria(activeField, {
      ...{ label: 'Active Flag', editable: true },
      ...props,
    }),
    knownActivities
  );

const worksheetIdCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(worksheetIdField, {
      ...{ label: 'Worksheet Id', editable: true },
      ...props,
    })
  );

export const departmentCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(departmentField, {
      ...{ label: 'Department', editable: true },
      ...props,
    })
  );

const ownerFirstCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(ownerFirstField, {
      ...{ label: 'Owner First Name', editable: true },
      ...props,
    })
  );

export const ownerLastCriteria = (props?: Partial<SearchFieldCriteria>) =>
  personFormCriteria(
    searchFieldCriteria(ownerLastField, {
      ...{ label: 'Provider', editable: true },
      ...props,
    })
  );

export const workflowOwnersLastCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  personFormCriteria(
    searchFieldCriteria(workflowOwnersLastField, {
      ...{ label: 'Workflow Owner', editable: true },
      ...props,
    })
  );

export const providerLastCriteria = (props?: Partial<SearchFieldCriteria>) =>
  personFormCriteria(
    searchFieldCriteria(providerLastField, {
      ...{ label: 'Provider', editable: true },
      ...props,
    })
  );

const patientFirstCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(patientFirstField, {
      ...{ label: 'Patient First', editable: true },
      ...props,
    })
  );

const patientLastCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(patientLastField, {
      ...{ label: 'Patient', editable: true },
      ...props,
    })
  );

export const patientDobCriteria = (props?: Partial<SearchFieldCriteria>) =>
  dateFormCriteria(
    searchFieldCriteria(patientDobField, {
      ...{ label: 'Patient DOB', editable: true },
      ...props,
    })
  );

const insuranceCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(insuranceField, {
      ...{ label: 'Insurance', editable: true },
      ...props,
    })
  );

export const completeCriteria = (props?: Partial<SearchFieldCriteria>) =>
  booleanCriteria(
    searchFieldCriteria(complete, {
      ...{ label: 'Complete', editable: true },
      ...props,
    })
  );

export const markedForDeleteCriteria = (props?: Partial<SearchFieldCriteria>) =>
  booleanCriteria(
    searchFieldCriteria(markedForDelete, {
      ...{ label: 'Deleted', editable: true },
      ...props,
    })
  );

export const bundleVersionCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(bundleVersion, {
      ...{ label: 'Bundle Version', editable: true, parseInt: true },
      ...props,
    })
  );

export const providerIdCriteria = (props?: Partial<SearchFieldCriteria>) =>
  textFormCriteria(
    searchFieldCriteria(providerId, {
      ...{ label: 'Provider ID', editable: true, parseInt: true },
      ...props,
    })
  );

const knownDocumentTypes = [
  { label: 'EMR_PHYSICIAN_TXT', name: 'EMR_PHYSICIAN_TXT' },
  { label: 'EMR_PHYSICIAN', name: 'EMR_PHYSICIAN' },
];

export const missingDocumentsCriteria = (
  props?: Partial<SearchFieldCriteria>
) =>
  multiSelectFormCriteria(
    searchFieldCriteria(missingRequiredDocumentTypes, {
      ...{ label: 'Missing Documents', editable: true },
      ...props,
    }),
    knownDocumentTypes
  );

const serviceDate = serviceDateCriteria();

const defaultDashboardFields = [
  locationCriteria(),
  accountCriteria(),
  mrnCriteria(),
  serviceDate,
  patientLastCriteria(),
  insuranceCriteria(),
];

export function dashboardSearchConfiguration(
  conf?: Partial<DashboardSearchConfiguration>
): DashboardSearchConfiguration {
  return {
    ...conf,
    ...{
      query: {},
      fields: defaultDashboardFields.concat(conf?.fields || []),
      filters: [],
      sort: [],
    },
  };
}

type DashboardSearchProps<T = any> = {
  fetch: (query: LocationQuery) => Promise<SearchResult>;
  location: () => { departmentCode: string; locationCode: string };
  configuration?: DashboardSearchConfiguration;
  detailView?: string; // 'visits || documents || worksheets',
  defaultSearch?: () => { predicate: string; fields: SearchField[] };
  searchSchemeField?: () => SearchField;
  onRequestPage?: (page: PageApi) => void;
  onRowClick?: (row: any) => void;
  rowIsSelected?: (rowData: T) => boolean;
};

export function searchSchemeField(
  range: DateRange,
  currentSearchScheme: string
): SearchField {
  return {
    fieldName: currentSearchScheme || 'createDate',
    startDate: utcDateString(range.startDate),
    endDate: utcDateString(range.endDate),
  };
}

// There should only be one search scheme date field
function updateSearchSchemeField(
  fields: SearchField[],
  searchScheme: SearchField
): SearchField[] {
  if (!fields) {
    return fields;
  }
  const updated = fields.filter(
    (a: SearchField) =>
      a.fieldName != 'createDate' &&
      a.fieldName != 'serviceDate' &&
      a.fieldName != 'registrationDate'
  );

  updated.push(searchScheme);
  return updated;
}

export function dashboardSearchApi<T = any>(
  a?: DashboardSearchProps<T>
): DashboardSearchApi {
  const props: Partial<DashboardSearchProps> = {
    ...{ configuration: dashboardSearchConfiguration() },
    ...a,
  };
  const { configuration, fetch, searchSchemeField, defaultSearch, location } =
    props;
  configuration.fields = configuration.fields.sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  const data = create<SearchData>(() => ({
    fields: configuration.fields,
    query: {},
    filters: [],
    sort: [],
    selectedFilter: null,
    openFilter: false,
    period: null,
    advancedActive: false,
    providers: [],
    stateFilters: knownCodingstates,
    result: null,
  }));

  const search = async () => {
    refreshQuery();
    const query = data.getState().query;

    if (searchSchemeField) {
      const field = searchSchemeField();
      query.filters.fields = updateSearchSchemeField(
        query.filters.fields as any,
        field
      );
    }

    if (!query.filters?.fields?.length && defaultSearch) {
      query.searchScheme = 'createDate';
      query.filters = defaultSearch() as any;
    }

    if (!query.locationCode) {
      return Promise.resolve({} as any);
    }
    const result = await fetch(query);
    data.setState({ result });
    return Promise.resolve(result);
  };

  const page = createPageApi();

  function hasField(
    list: SearchFieldCriteria[],
    v: SearchFieldCriteria
  ): boolean {
    return list.filter((u) => u.label === v.label).length > 0;
  }

  const hasFilter = (v: SearchFieldCriteria) =>
    hasField(data.getState().filters, v);
  const hasSort = (v: SearchFieldCriteria) => hasField(data.getState().sort, v);

  const refreshFields = () => ({ fields: data.getState().fields.slice() });
  const refreshSort = () => ({ sort: data.getState().sort.slice() });

  function updateOrAdd(
    list: SearchFieldCriteria[],
    field: SearchField,
    valueFn?: () => any
  ): SearchField {
    let item: SearchFieldCriteria = list.filter(
      (u) => u.field.fieldName === field.fieldName
    )[0];

    if (!item) {
      item = data
        .getState()
        .fields.filter((u) => u.field.fieldName === field.fieldName)[0];
      if (!item) {
        throw new Error(
          field.fieldName + ' is not in the visit search configuration'
        );
      } else {
        if (valueFn) {
          const value = valueFn();
          item.form.controls.control.value.setState({ value });
        }
      }
      list.push(item);
    }
    item.field = { ...item.field, ...field };
    return item.field;
  }

  const updateOrAddFilter = (field: SearchField, valueFn?: () => any) => {
    const v = updateOrAdd(data.getState().filters, field, valueFn);
    resetToFirstPage();

    refreshSort();

    return v;
  };

  const updateOrAddSort = (field: SearchField) => {
    const v = updateOrAdd(data.getState().sort, field);
    refreshFields();
    return v;
  };

  let currentColumnSort: SearchField;
  let currentPredicate: SearchPredicate = 'OR';

  const refreshQuery = () => {
    const loc = location();
    const defaults = {
      departmentCode: loc.departmentCode,
      locationCode: loc.locationCode,
    };

    let query: VisitSearchQuery = {
      ...(data.getState().query || {}),
      ...defaults,
    };

    const predicate = currentPredicate || 'OR';
    const advanced = data.getState().advancedActive;
    const { search } = { ...data.getState().period };

    query.queryString = search ? search : undefined;

    if (query.queryString) {
      query.queryString = query.queryString.replace(/\t/g, '');
    }

    if (advanced) {
      const filters = data.getState().filters;
      query.filters = {
        predicate,
        fields: filters.map((v) => v.field),
      };
      query.sort = data.getState().sort.map((v) => ({
        fieldName: v.field.fieldName,
        sortOrder: isNaN(v.field.sortOrder) ? 1 : v.field.sortOrder,
      }));
    } else {
      query.filters = undefined;

      if (currentColumnSort) {
        query.sort = [
          {
            fieldName: currentColumnSort.fieldName,
            sortOrder: isNaN(currentColumnSort.sortOrder)
              ? 1
              : currentColumnSort.sortOrder,
          },
        ];
      }
    }
    const defaultFilter = {
      predicate: 'OR',
      fields: [],
    };
    query.filters = query.filters || (defaultFilter as any);
    query.sort = query.sort && query.sort.length ? query.sort : undefined;

    if (query.filters?.fields?.length) {
      let fields = [];
      query.filters.fields.forEach((v) => {
        v.sortOrder = undefined;

        if (v.fieldName == 'createLocal') {
          fields.push(v);
          return;
        }

        if (!v?.stringFilter?.trim) {
          fields.push(v);
          return;
        }

        v.stringFilter = v.stringFilter && v.stringFilter.trim();

        if (v.stringFilter?.length && v.stringFilter.indexOf(',') > 0) {
          const stringFilters = v.stringFilter
            .split(',')
            .map((a: string) => a.trim());

          fields.push({ ...v, ...{ stringFilter: stringFilters } });
        } else {
          fields.push(v);
        }
        query.filters.fields = fields;
      });
    } else {
      query.filters.fields = [];
    }
    //  console.log('refreshQuery', query);

    data.setState({ query });
    return query;
  };
  const setQuery = (query: VisitSearchQuery) => {
    data.setState({ query: { ...refreshQuery(), ...query } });
  };
  const openFilter = (selectedFilter: SearchFieldCriteria) => {
    data.setState({ selectedFilter, openFilter: true });
  };
  const closeFilter = () => {
    data.setState({ openFilter: false });
  };
  const reset = () => {
    const filters = data.getState().filters;
    filters.forEach((v: SearchFieldCriteria) => {
      if (v.field.fieldName === worksheetStateField.fieldName) {
        const c = v?.form?.controls?.control;
        if (c) {
          c.clear();
        }
      }
    });

    data.setState({ filters: [], sort: [] });
  };

  const onRequestSort: (event: any, column: TableColumn<T>) => void = (
    sortOrder: any,
    c: TableColumn
  ) => {
    if (data.getState().advancedActive) {
      updateOrAddSort({ ...{ sortOrder }, ...c.data });
    } else {
      updateColumnSort({ ...{ sortOrder }, ...c.data });
    }
    search();
  };

  const onRequestPage: (page: Page) => void = ({ pageNumber, pageSize }) => {
    setQuery({ pageNumber, pageSize });
    search();
  };

  const onRowClick: (row: any) => void = (r) => {
    if (props.onRowClick) {
      props.onRowClick(r);
    }
  };

  const rowIsSelected = props.rowIsSelected ? props.rowIsSelected : () => false;

  const updateColumnSort = (v: SearchField) => {
    currentColumnSort = v;
    return v;
  };

  function toggleAdvanced() {
    const advancedActive = !data.getState().advancedActive;
    data.setState({ advancedActive });
  }

  function openIfFilters() {
    const len = data.getState().filters?.length;
    if (len > 0) {
      data.setState({ advancedActive: true });
    }
  }

  function resetToFirstPage() {
    page.setState({ pageNumber: 0 });
    data.setState({
      result: {
        totalRecords: 0,
        rows: [],
      },
    });
    setQuery({ pageNumber: 0 });
  }

  function setFieldValue(field: SearchField) {
    const item = data
      .getState()
      .fields.filter((u) => u.field.fieldName === field.fieldName)[0];
    if (item && item.setValue) {
      item.field = { ...field };
      item.setValue(field);
    }
  }

  function addFilters(fields: SearchField[], append = false) {
    if (!fields.length) {
      return;
    }
    let filters = append ? data.getState().filters : [];

    const a = fields.slice();
    a.forEach((v) => {
      const c: SearchFieldCriteria = configuration.fields.find(
        (c) => c.field.fieldName === v.fieldName
      );
      const exists = filters.find((a) => a.field.fieldName === v.fieldName);

      setFieldValue(v);

      if (c) {
        c.field = v;
        if (!exists) {
          filters.push(c);
        }
      }
    });

    resetToFirstPage();
    data.setState({ filters: filters.slice() });
  }

  function findCriteria(fieldName: string): SearchFieldCriteria {
    return configuration.fields.find((c) => c.field.fieldName === fieldName);
  }

  function resetCriteria(a: SearchFieldCriteria) {
    if (a) {
      if (a.reset) {
        a.reset();
      }
    }
  }

  function refreshFilters() {
    data.setState({ filters: (data.getState().filters || []).slice() });
  }
  const actions: SearchActions = {
    search,
    hasFilter,
    hasSort,
    setQuery,
    updateOrAddFilter,
    updateOrAddSort,
    updateColumnSort,
    openFilter,
    closeFilter,
    reset,
    toggleAdvanced,
    openIfFilters,
    resetToFirstPage,
    addFilters,
    refreshFilters,
    findCriteria,
    deleteFilter: (v) => {
      resetCriteria(v);

      const list = data.getState().filters;
      const filters = list.filter(
        (u) => u.field.fieldName !== v.field.fieldName
      );
      data.setState({ ...{ filters }, ...refreshFields() });
      search();
    },
    addFilter: (v) => {
      const filters = data.getState().filters;
      if (!hasField(filters, v)) {
        data.setState({
          ...{ filters: filters.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      resetToFirstPage();
      search();
      closeFilter();
    },
    updateFilter: (v) => {
      const filters = data.getState().filters;
      filters.forEach((u) => {
        if (u.label === v.label) {
          u.field = { ...u.field, ...v.field };
        }
      });
      search();
    },
    deleteSort: (v) => {
      const list = data.getState().sort;
      const sort = list.filter((u) => u.label !== v.label);
      data.setState({ ...{ sort }, ...refreshFields() });
      search();
    },
    addSort: (v) => {
      const sort = data.getState().sort;
      if (!hasField(sort, v)) {
        data.setState({
          ...{ sort: sort.concat({ ...v }) },
          ...refreshFields(),
        });
      }
      search();
    },
    updateSort: (v) => {
      const sort = data.getState().sort;
      sort.forEach((u) => {
        if (u.label === v.label) {
          u.field.sortOrder = v.field.sortOrder;
        }
      });

      search();
    },
  };

  const onPeriodSearch = async (args: PeriodSearch) => {
    data.setState({ period: args });
    search();
  };

  const predicate = formApi<{ predicate: SearchPredicate }>({
    values: { predicate: 'OR' },
    fields: {
      predicate: {
        style: { height: 48 },
        options: [
          { value: 'OR', label: 'Or' },
          { value: 'AND', label: 'And' },
        ],
      },
    },
    onChange: (v) => {
      currentPredicate = v.predicate;
      refreshQuery();
    },
  });

  const forms = { predicate };
  const provider = {};

  return {
    configuration,
    data,
    actions,
    forms,
    page,
    provider,
    onPeriodSearch,
    onRequestSort,
    onRequestPage,
    onRowClick,
    rowIsSelected,
  };
}
